import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { siteConfig } from "../siteConfig";
import Reveal from "../components/Reveal";

const { blog, colors, hero } = siteConfig;

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blog.posts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-5" style={{ backgroundColor: colors.light }}>
        <h1 className="font-display font-black text-3xl mb-4" style={{ color: colors.dark }}>Post Not Found</h1>
        <p className="text-gray-500 mb-8">That blog post doesn't exist or may have been removed.</p>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 font-bold text-sm"
          style={{ color: colors.primary }}
        >
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Hero */}
      <section
        className="relative flex items-end"
        style={{
          minHeight: "40vh",
          backgroundImage: `url(${hero.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.45) 100%)" }} />
        <div className="relative z-10 w-full max-w-3xl mx-auto px-5 md:px-8 pb-12 pt-32">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest mb-6 hover:opacity-80 transition-opacity"
            style={{ color: colors.primary }}
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Blog
          </Link>
          <h1
            className="font-display font-black text-white leading-tight"
            style={{ fontSize: "clamp(1.75rem,4vw,3rem)" }}
          >
            {post.title}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24" style={{ backgroundColor: colors.light }}>
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          {/* Excerpt lead */}
          <Reveal
            as="p"
            className="text-lg leading-relaxed font-medium mb-10 pb-10 border-b border-gray-200"
            style={{ color: colors.dark }}
          >
            {post.excerpt}
          </Reveal>

          {/* Content blocks */}
          <div className="space-y-6">
            {post.content.map((block, i) => {
              if (block.type === "h2") {
                return (
                  <h2
                    key={i}
                    className="font-display font-black text-xl md:text-2xl pt-4"
                    style={{ color: colors.dark }}
                  >
                    {block.text}
                  </h2>
                );
              }
              if (block.type === "ul") {
                return (
                  <ul key={i} className="space-y-3 pl-1">
                    {block.items.map((item, j) => (
                      <li key={j} className="flex gap-3 text-gray-600 text-base leading-relaxed">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: colors.primary }} />
                        {item}
                      </li>
                    ))}
                  </ul>
                );
              }
              return (
                <p key={i} className="text-gray-600 text-base leading-relaxed">
                  {block.text}
                </p>
              );
            })}
          </div>

          {/* CTA footer */}
          <Reveal
            className="mt-16 rounded-2xl p-8 text-center"
            style={{ backgroundColor: colors.dark }}
          >
            <h3 className="font-display font-black text-white text-xl mb-3">
              Ready to Transform Your Outdoor Space?
            </h3>
            <p className="text-white/60 text-sm mb-6">
              Get a free, no-obligation estimate from our team.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-white text-sm transition-opacity hover:opacity-85"
              style={{ backgroundColor: colors.primary }}
            >
              Get a Free Quote
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default BlogPostPage;
